import { FC, useCallback, useState, useEffect } from 'react';
import { useRoomContext } from "@livekit/components-react";
import { Track } from 'livekit-client';
import cn from "classnames";
import { ButtonGroup } from "../../../components/ButtonGroup";
import Icon from "../../../components/Icon";
import {
  useStartStudioBroadcastMutation,
  useStopStudioBroadcastMutation
} from "../../../features/api/apiSlice";
import FunctionalButtonProps from "./FunctionalButtons.interface";
import styles from "./FunctionalButtons.module.css";
import TrackToggleButton from "./TrackToggleButton";
import { useScreenWidth } from '../../../hooks/useScreenWidth';

const FunctionalButtons: FC<FunctionalButtonProps> = ({ onSettings, studioStatus }) => {
  const { isMobile } = useScreenWidth();
  const room = useRoomContext();
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [broadcastError, setBroadcastError] = useState<string | null>(null);

  const [startStudioBroadcast, { isLoading: isStartLoading }] = useStartStudioBroadcastMutation();
  const [stopStudioBroadcast, { isLoading: isStopLoading }] = useStopStudioBroadcastMutation();

  const isLoading = isStartLoading || isStopLoading;

  useEffect(() => {
    setIsBroadcasting(studioStatus === "online");
  }, [studioStatus]);

  const handleToggleBroadcast = useCallback(async () => {
    if (isLoading) return;

    setBroadcastError(null);

    try {
      if (isBroadcasting || studioStatus === "online") {
        await stopStudioBroadcast().unwrap();
        setIsBroadcasting(false);
      } else {
        await startStudioBroadcast().unwrap();
        setIsBroadcasting(true);
      }
    } catch (error) {
      console.error("Ошибка при управлении трансляцией:", error);
      setBroadcastError(
        error instanceof Error
          ? error.message
          : 'Для того, чтобы начать трансляцию включите хотя бы один канал'
      );
    }
  }, [isBroadcasting, startStudioBroadcast, stopStudioBroadcast, isLoading]);

  const getBroadcastButtonText = (includeError = true) => {
    if (isLoading) {
      return includeError ? "Обработка запроса..." : "Обработка запроса";
    }

    if (includeError && broadcastError) {
      return broadcastError;
    }

    if (isBroadcasting) {
      return "Остановить трансляцию";
    }

    return "Начать трансляцию";
  };

  if (!room) {
    return null;
  }

  return (
    <div className={styles["functional-buttons"]}>
      <ButtonGroup>
        <ul className={styles["functional-buttons__button-group"]}>
          {!isMobile &&
            <TrackToggleButton
              trackSource={Track.Source.ScreenShare}
              iconName="studio-screen-share"
              tooltipEnabled="Остановить демонстрацию экрана"
              tooltipDisabled="Демонстрация экрана"
            />
          }

          <TrackToggleButton
            trackSource={Track.Source.Microphone}
            iconName="studio-microphone"
            tooltipEnabled="Выключить микрофон"
            tooltipDisabled="Включить микрофон"
          />

          <TrackToggleButton
            trackSource={Track.Source.Camera}
            iconName="studio-camera"
            tooltipEnabled="Выключить камеру"
            tooltipDisabled="Включить камеру"
          />

          <li
            role="button"
            className={styles["functional-buttons__button"]}
            onClick={onSettings}
            title="Настройки"
            aria-label="Настройки"
          >
            <div className={styles["functional-buttons__icon-wrapper"]}>
              <Icon name="studio-settings" />
            </div>
          </li>

          <li
            role="button"
            className={cn(
              styles["functional-buttons__button"],
              { [styles["functional-buttons__button--active"]]: isBroadcasting },
              { [styles["functional-buttons__button--error"]]: !!broadcastError },
              { [styles["functional-buttons__button--disabled"]]: isLoading },
              { [styles["functional-buttons__button--loading"]]: isLoading }
            )}
            onClick={handleToggleBroadcast}
            title={getBroadcastButtonText(true)}
            aria-label={getBroadcastButtonText(false)}
            aria-disabled={isLoading}
          >
            <div
              className={cn(
                styles["functional-buttons__icon-wrapper"],
                { [styles["functional-buttons__icon-wrapper--active"]]: isBroadcasting },
                { [styles["functional-buttons__icon-wrapper--error"]]: !!broadcastError },
                { [styles["functional-buttons__icon-wrapper--disabled"]]: isLoading }
              )}
            >
              {isLoading ? (
                <div className={styles["functional-buttons__loading-indicator"]} />
              ) : (
                <Icon name="studio-broadcast" />
              )}
            </div>
            {broadcastError && (
              <div className={styles["functional-buttons__error-tooltip"]}>
                {broadcastError}
              </div>
            )}
          </li>
        </ul>
      </ButtonGroup>
    </div>
  );
};

export default FunctionalButtons;